/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { createSelector, createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../reducers';
import G17Client from '../services/G17Client';
import { SurveyListItem } from '../types/survey';
import { SurveyType } from '@g17eco/types/surveyCommon';
import { StandardsRating } from '../types/standard';
import { DataPeriods } from '@utils/dataPeriods';

interface LoadPayload {
  initiativeId: string
}

const SLICE_NAME = 'initiativeSurveyList';

interface SurveyListState {
  loaded: boolean;
  errored: boolean;
  errorMessage: string;
  code: string;
  data: SurveyListItem[];
  standards: StandardsRating[],
  initiativeId?: string
}

interface ResponseData {
  list: SurveyListItem[],
  standards: StandardsRating[]
}

const getInitialState = function (): SurveyListState {
  return {
    loaded: false,
    errored: false,
    errorMessage: '',
    code: '',
    initiativeId: undefined,
    data: [],
    standards: []
  };
};
const initiativeSurveyListSlice = createSlice({
  name: SLICE_NAME,
  initialState: getInitialState(),
  reducers: {
    load: (_state, action: PayloadAction<LoadPayload>) => {
      return { ...getInitialState(), ...action.payload };
    },
    loaded: (state, { payload }: PayloadAction<ResponseData>) => {
      return { ...state, loaded: true, data: payload.list, standards: payload.standards }
    },
    error: (state, action: PayloadAction<Error>) => {
      return { ...state, errored: true, errorMessage: action.payload.message };
    },
    unload: () => getInitialState(),
  },
});

const { load, loaded, error, unload } = initiativeSurveyListSlice.actions;

export default initiativeSurveyListSlice.reducer;

export const selectList = (state: RootState): SurveyListState => state[SLICE_NAME];

export const selectWithDefault = createSelector([selectList], (state) => {
  return [{ _id: '', name: 'Current Total' }, ...state.data] as SurveyListItem[];
});

export const selectDefaultSurvey = createSelector([selectList], (state) => {
  const list = state.data;
  if (list.length === 0) {
    return;
  }

  const completedSurveys = list.filter(data => !!data.completedDate);
  if (completedSurveys?.[0]) {
    return completedSurveys[0];
  }

  const notCompletedSurveys = list.filter(data => !data.completedDate && data._id);
  if (notCompletedSurveys?.[0]) {
    return notCompletedSurveys[0];
  }
});

export const selectMostRecentSurvey = createSelector([selectList], (state: SurveyListState) => {
  const list = state.data;
  if (list.length === 0) {
    return;
  }

  // Should be default, as now we also have auto-aggregated
  return list.find(data => data.type == SurveyType.Default);
});

export const selectMostRecentSurveyByPeriodAndType = createSelector(
  [
    selectList,
    (_state: RootState, period: DataPeriods | undefined) => period,
    (_state: RootState, _period: DataPeriods | undefined, type: SurveyType) => type,
  ],
  (state, period, type) => {
    const list = state.data;
    if (list.length === 0) {
      return;
    }
    if (!period) {
      return list.find((data) => data.type === type);
    }
    return list.find((data) => data.type === type && data.period === period);
  }
);

export const unloadInitiativeSurveyList = () => (dispatch: Dispatch) => dispatch(unload())

export const reloadSurveyList = () =>
  (dispatch: any, getState: () => RootState) => {

    const state = getState()[SLICE_NAME];
    const initiativeId = state.initiativeId;

    if (!initiativeId) {
      return;
    }

    dispatch(loadSurveyList(initiativeId, true));
  };

export const loadSurveyList = (initiativeId: string, forceRefresh: boolean = false) =>
  (dispatch: any, getState: () => RootState) => {

    const state = getState()[SLICE_NAME];
    if (!forceRefresh && initiativeId === state.initiativeId) {
      return;
    }

    dispatch(load({ initiativeId }));
    return G17Client.getSurveyListWithStandards(initiativeId)
      .then((data) => {
        dispatch(loaded(data))
      },
        (err: Error) => {
          dispatch(error(err));
        }
      )
  };
